import { comuneByIstat, gazetteer } from '@sse/gazetteer';
import { normalizePlace } from '@sse/gazetteer/normalize';
import { useEffect, useMemo, useRef, useState } from 'react';
import { inputCls } from './ui';

/** Scelta del comune con ricerca (gazetteer: 145 comuni delle province di Lecce, Brindisi e Taranto). */
export function ComunePicker({
  value,
  onChange,
  allowEmpty = false,
  placeholder = 'Cerca un comune…',
  id,
}: {
  value: string | undefined;
  onChange: (istat: string | undefined) => void;
  allowEmpty?: boolean;
  placeholder?: string;
  id?: string;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const selected = value ? comuneByIstat(value) : undefined;
  const list = useMemo(() => {
    const t = normalizePlace(q);
    const all = gazetteer.comuni.filter((c) => !t || normalizePlace(c.name).includes(t));
    return all.slice(0, 50);
  }, [q]);
  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [open]);
  const pick = (istat: string | undefined) => {
    onChange(istat);
    setOpen(false);
    setQ('');
  };
  return (
    <div ref={ref} className="relative">
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className={inputCls}
        placeholder={selected ? `${selected.name} (${selected.province})` : placeholder}
        value={open ? q : selected ? `${selected.name} (${selected.province})` : ''}
        onFocus={() => {
          setOpen(true);
          setActive(0);
        }}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setActive((a) => Math.min(a + 1, list.length - 1));
          else if (e.key === 'ArrowUp') setActive((a) => Math.max(a - 1, 0));
          else if (e.key === 'Enter' && open && list[active]) {
            e.preventDefault();
            pick(list[active].istat);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto border border-fg/25 bg-page shadow-lg"
        >
          {allowEmpty && (
            <li>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-muted hover:bg-surface"
                onClick={() => pick(undefined)}
              >
                Tutti i comuni
              </button>
            </li>
          )}
          {list.map((c, i) => (
            <li key={c.istat}>
              <button
                type="button"
                role="option"
                aria-selected={c.istat === value}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(c.istat)}
                className={`flex w-full justify-between px-3 py-2 text-left text-sm ${i === active ? 'bg-surface' : ''} ${c.istat === value ? 'font-medium' : ''}`}
              >
                <span>{c.name}</span>
                <span className="text-muted">{c.province}</span>
              </button>
            </li>
          ))}
          {list.length === 0 && <li className="px-3 py-2 text-sm text-muted">Nessun comune</li>}
        </ul>
      )}
    </div>
  );
}
